import type { ReviewItemId } from '@/lib/domain/ids';
import { inspectPollContent, POLL_MAX_OPTIONS, POLL_MIN_OPTIONS, validateItem } from './behaviors';
import type { EditionItem, PollOption, ReviewItem } from './types';

function resolveItemType(item: ReviewItem): 'trivia' | 'poll' {
  return item.itemType === 'poll' ? 'poll' : 'trivia';
}

export function canPersistReviewItem(item: ReviewItem): boolean {
  return !item.status;
}

export function fromReviewItem(item: ReviewItem, index: number): EditionItem {
  if (resolveItemType(item) === 'poll') {
    const pollItem = {
      type: 'poll' as const,
      context: item.context,
      membershipId: undefined,
      questionIndex: index,
      includedInEdition: item.includedInEdition ?? true,
      valid: item.valid,
      errors: item.errors,
      pollDefinitionId: item.pollDefinitionId,
      pollPrompt: item.pollPrompt ?? '',
      pollOptions: item.pollOptions ?? [],
      pollCanEditDefinitionInline: item.pollCanEditDefinitionInline,
    };
    const validation = validateItem(pollItem);

    return {
      ...pollItem,
      valid: item.valid ?? validation.valid,
      errors: Object.keys(item.errors).length > 0 ? item.errors : validation.errors,
    };
  }

  const triviaItem = {
    type: 'trivia' as const,
    membershipId: undefined,
    questionIndex: index,
    includedInEdition: item.includedInEdition ?? true,
    valid: item.valid,
    errors: item.errors,
    content: item.content,
    context: item.context,
    group: item.group,
  };
  const validation = validateItem(triviaItem);

  return {
    ...triviaItem,
    valid: item.valid ?? validation.valid,
    errors: Object.keys(item.errors).length > 0 ? item.errors : validation.errors,
  };
}

function emptyPollContent(): ReviewItem['content'] {
  return {
    stem: '',
    key: '',
    distractor_1: '',
    distractor_2: '',
    distractor_3: '',
    question_fact: '',
  };
}

export function toReviewItem(item: EditionItem, id: ReviewItemId): ReviewItem {
  if (item.type === 'poll') {
    return {
      id,
      itemType: 'poll',
      context: item.context,
      content: emptyPollContent(),
      valid: item.valid,
      errors: item.errors,
      selected: false,
      includedInEdition: item.includedInEdition,
      pollDefinitionId: item.pollDefinitionId,
      pollPrompt: item.pollPrompt,
      pollOptions: item.pollOptions,
      pollCanEditDefinitionInline: item.pollCanEditDefinitionInline,
    };
  }

  return {
    id,
    itemType: 'trivia',
    content: item.content,
    context: item.context,
    group: item.group,
    valid: item.valid,
    errors: item.errors,
    selected: false,
    includedInEdition: item.includedInEdition,
  };
}

export function parseMembershipIdFromReviewItem(
  item: ReviewItem,
  parseEditionItemReviewItemId: (id: ReviewItemId) => string | null
): string | undefined {
  const membershipId = parseEditionItemReviewItemId(item.id);
  return membershipId ?? undefined;
}

export function withMembershipId(item: EditionItem, membershipId?: string): EditionItem {
  return {
    ...item,
    membershipId,
  };
}

export function normalizePollOptions(options: PollOption[] | undefined): PollOption[] {
  return (options ?? []).map((option, index) => ({
    ...(option.id ? { id: option.id } : {}),
    label: option.label,
    sort_index: option.sort_index ?? index,
    ...(option.hidden !== undefined ? { hidden: option.hidden } : {}),
    ...(option.hiddenAt !== undefined ? { hiddenAt: option.hiddenAt } : {}),
  }));
}

/** Portal editor validation — maps poll fields onto ReviewItem error keys. */
export function validatePollReviewItem(item: Pick<ReviewItem, 'pollPrompt' | 'pollOptions'>): {
  valid: boolean;
  errors: ReviewItem['errors'];
} {
  const errors: ReviewItem['errors'] = {};
  const pollContent = inspectPollContent({
    pollPrompt: item.pollPrompt ?? '',
    pollOptions: item.pollOptions ?? [],
  });

  if (pollContent.promptMissing) {
    errors.stem = 'This field is required';
  }

  if (pollContent.filledOptionCount < POLL_MIN_OPTIONS) {
    errors.options = `At least ${POLL_MIN_OPTIONS} options are required`;
  } else if (pollContent.filledOptionCount > POLL_MAX_OPTIONS) {
    errors.options = `Maximum ${POLL_MAX_OPTIONS} options allowed`;
  } else if (pollContent.hasDuplicateOptionLabels) {
    errors.options = 'Options must be unique';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export function fingerprintPollFields(item: ReviewItem) {
  if (item.itemType !== 'poll') {
    return null;
  }

  return {
    itemType: 'poll' as const,
    pollDefinitionId: item.pollDefinitionId ?? null,
    pollPrompt: item.pollPrompt ?? '',
    pollOptions: normalizePollOptions(item.pollOptions).map((option) => ({
      id: option.id ?? null,
      label: option.label,
      sort_index: option.sort_index,
    })),
  };
}
